type DateFactoryMethod = 'toDate' | 'toJSDate';
const isoDateRegex =
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

const isValidDate = (value: unknown): value is Date => {
    return value instanceof Date && !Number.isNaN(value.getTime());
};

const normalizePrimitiveDateLike = (value: unknown): Date | null => {
    if (isValidDate(value)) {
        return value;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        const fromMillis = new Date(value);
        return isValidDate(fromMillis) ? fromMillis : null;
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (!trimmedValue || !isoDateRegex.test(trimmedValue)) {
            return null;
        }

        const parsedMillis = Date.parse(trimmedValue);
        if (Number.isNaN(parsedMillis)) {
            return null;
        }

        const fromIso = new Date(parsedMillis);
        return isValidDate(fromIso) ? fromIso : null;
    }

    return null;
};

const tryGetDateFromMethod = (value: unknown, method: DateFactoryMethod): Date | null => {
    if (!value || typeof value !== 'object' || !(method in value)) {
        return null;
    }

    const dateFactoryMethod = (value as Record<string, unknown>)[method];

    if (typeof dateFactoryMethod !== 'function') {
        return null;
    }

    try {
        const candidate = dateFactoryMethod.call(value);
        return normalizePrimitiveDateLike(candidate);
    } catch {
        return null;
    }
};

export const normalizeDateLike = (value: unknown): Date | null => {
    if (value === null || value === undefined) {
        return null;
    }

    const fromPrimitive = normalizePrimitiveDateLike(value);
    if (fromPrimitive) {
        return fromPrimitive;
    }

    const fromLuxonLikeValue = tryGetDateFromMethod(value, 'toJSDate');
    if (fromLuxonLikeValue) {
        return fromLuxonLikeValue;
    }

    const fromMomentLikeValue = tryGetDateFromMethod(value, 'toDate');
    if (fromMomentLikeValue) {
        return fromMomentLikeValue;
    }

    return null;
};
